import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Calculator, Leaf, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalculateModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    landSize: '',
    landUnit: 'acres',
    residueType: ''
  });
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const residueTypes = [
    'Rice Straw',
    'Wheat Straw',
    'Corn Stalks',
    'Sugarcane Bagasse',
    'Cotton Stalks',
    'Other Agricultural Waste'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/calculate-potential`, formData);
      setResult(response.data);
      toast({
        title: 'Calculation Complete!',
        description: 'See your potential impact below'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to calculate',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ landSize: '', landUnit: 'acres', residueType: '' });
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Calculator className="w-6 h-6 text-neo-emerald" />
            Calculate Your Potential
          </DialogTitle>
          <DialogDescription>
            Estimate your carbon credit earnings and environmental impact
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label htmlFor="landSize">Land Size *</Label>
              <div className="flex gap-2">
                <Input
                  id="landSize"
                  type="number"
                  step="0.1"
                  placeholder="Enter land size"
                  value={formData.landSize}
                  onChange={(e) => setFormData({ ...formData, landSize: e.target.value })}
                  required
                  className="flex-1"
                />
                <Select 
                  value={formData.landUnit} 
                  onValueChange={(value) => setFormData({ ...formData, landUnit: value })}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="acres">Acres</SelectItem>
                    <SelectItem value="hectares">Hectares</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="residueType">Residue Type *</Label>
              <Select 
                value={formData.residueType} 
                onValueChange={(value) => setFormData({ ...formData, residueType: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select residue type" />
                </SelectTrigger>
                <SelectContent>
                  {residueTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-neo-emerald/10 border border-neo-emerald/20 rounded-lg p-4">
              <p className="text-sm text-neo-gray font-body">
                <strong>Note:</strong> This is an estimate based on average values. Actual results may vary based on soil conditions, climate, and farming practices.
              </p>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-neo-emerald hover:bg-[#1db868] text-neo-white glow-emerald-hover" disabled={isLoading}>
                {isLoading ? 'Calculating...' : 'Calculate Potential'}
              </Button>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-6 mt-4">
            <div className="bg-gradient-to-br from-neo-emerald/10 to-neo-emerald/5 border border-neo-emerald/20 rounded-2xl p-6 space-y-4">
              <h3 className="text-xl font-heading font-bold text-neo-black mb-4">Your Potential Impact</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-neo-emerald/20 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-neo-emerald/20 rounded-lg flex items-center justify-center">
                      <Leaf className="w-5 h-5 text-neo-emerald" />
                    </div>
                    <div>
                      <p className="text-sm text-neo-gray font-body">CO₂ Sequestration</p>
                      <p className="text-2xl font-heading font-bold text-neo-emerald">{result.co2Sequestration} tons/year</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-neo-emerald/20 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-neo-emerald/20 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-neo-emerald" />
                    </div>
                    <div>
                      <p className="text-sm text-neo-gray font-body">Annual Earnings</p>
                      <p className="text-2xl font-heading font-bold text-neo-emerald">₹{result.estimatedEarnings.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-neo-emerald/20 rounded-xl p-4">
                <h4 className="font-heading font-semibold text-neo-black mb-3">Additional Benefits:</h4>
                <ul className="space-y-2 text-sm text-neo-gray font-body">
                  <li>• Soil health improvement: <strong className="text-neo-emerald">{result.soilImprovement}%</strong></li>
                  <li>• Water retention increase: <strong className="text-neo-emerald">{result.waterRetention}%</strong></li>
                  <li>• Biochar production: <strong className="text-neo-emerald">{result.biocharProduction} tons/year</strong></li>
                  <li>• Estimated setup time: <strong className="text-neo-emerald">2-3 minutes</strong></li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                className="flex-1 bg-neo-emerald hover:bg-[#1db868] text-neo-white glow-emerald-hover"
                onClick={handleClose}
              >
                Start Setup Now
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setResult(null);
                  setFormData({ landSize: '', landUnit: 'acres', residueType: '' });
                }}
              >
                Calculate Again
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CalculateModal;
